/**
 * Signup Flow Component
 * Multi-step signup form
 * 
 * @author Varun Pandey
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { PhoneInput } from '../../components/ui/PhoneInput';
import { OTPInput } from '../../components/ui/OTPInput';
import { Select } from '../../components/ui/Select';
import { MultiSelect } from '../../components/ui/MultiSelect';
import { Loader } from '../../components/ui/Loader';
import { Sheet } from '../../components/ui/Sheet';
import { ArrowRight, Pencil } from 'lucide-react';
import { AuthLayout } from '../../components/layout/AuthLayout';
import { SimilarProfiles, SimilarProfile } from '../../components/features/SimilarProfiles';
import { ResetPasswordModal } from '../../components/features/ResetPasswordModal';
import { useVideoStore } from '../../stores/videoStore';
import { useArtistDetailsStore } from '../../stores/artistDetailsStore';
import { AuthService, getAuthErrorMessage, handleSsoResult } from '../../services/authService';
import { GoogleLogin } from '@react-oauth/google';
import { cn } from '../../utils/cn';
import { setAuthRoleCookies } from '../../utils/cookies';
import { Category } from '../../api/types';
import { storageManager } from '../../storage/storageManager';
import { STORAGE_KEYS } from '../../storage/storageKeys';
import toast from 'react-hot-toast';
import { canUseTruecaller, generateTruecallerRequestNonce, buildTruecallerDeepLink } from '../../utils/truecaller';
import { INDIAN_PHONE_ERROR_MESSAGE, isValidIndianPhoneNumber } from '../../utils/phone';
import TrueCallerIcon from '../../assets/images/TrueCallerIcon.png';
import { InstagramAuthCore } from '@/instagram/InstagramAuthCore';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { GoogleAuthCore } from '@/auth/GoogleAuthCore';
import { AppleAuthCore } from '@/auth/AppleAuthCore';
import { getAppleSignInProfile, clearAppleSignInProfile } from '@/auth/appleSignInStorage';

// iOS-optimized easing curves for step transitions (typed as tuples for Framer Motion)
const iosEasing: [number, number, number, number] = [0.25, 0.1, 0.25, 1.0];
const iosExitEasing: [number, number, number, number] = [0.4, 0.0, 1, 1];

// Step transition variants - iOS-style slide
const stepVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 30 : -30,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: {
      duration: 0.35,
      ease: iosEasing,
    },
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -30 : 30,
    opacity: 0,
    transition: {
      duration: 0.25,
      ease: iosExitEasing,
    },
  }),
};

// Step 1: Create your artist profile
const Step1: React.FC<{
  formData: SignupFormData;
  updateFormData: (data: Partial<SignupFormData>) => void;
  onNext: () => void;
  onBack?: () => void;
  onGoogleSuccess?: (credentialResponse: { credential?: string }) => void;
  onNativeGoogleClick?: () => void;
  isSsoLoading?: boolean;
  onSsoStart?: () => void;
  onTruecallerClick?: () => void;
  onInstagramClick?: () => void;
  onAppleClick?: () => void;
}> = ({
  formData,
  updateFormData,
  onNext,
  onGoogleSuccess,
  onNativeGoogleClick,
  isSsoLoading,
  onSsoStart,
  onTruecallerClick,
  onInstagramClick,
  onAppleClick,
}) => {
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isCheckingEmail, setIsCheckingEmail] = useState(false);
    const emailRef = useRef<HTMLInputElement>(null);
    const passwordRef = useRef<HTMLInputElement>(null);
    const confirmPasswordRef = useRef<HTMLInputElement>(null);

    // Focus first error field
    useEffect(() => {
      if (Object.keys(errors).length > 0) {
        let input: HTMLInputElement | null = null;

        if (errors.email && emailRef.current) {
          input = emailRef.current;
        } else if (errors.password && passwordRef.current) {
          input = passwordRef.current;
        } else if (errors.confirmPassword && confirmPasswordRef.current) {
          input = confirmPasswordRef.current;
        }

        if (input) {
          // Use setTimeout to ensure DOM is updated
          setTimeout(() => {
            input?.focus();
            // Position cursor at end of text - user can immediately start typing
            if (input && input.setSelectionRange) {
              const length = input.value.length;
              input.setSelectionRange(length, length);
            }
          }, 0);
        }
      }
    }, [errors]);

    const isEmailValid = () => {
      if (!formData.email) return false;
      return /\S+@\S+\.\S+/.test(formData.email);
    };

    const validate = () => {
      const newErrors: Record<string, string> = {};

      if (!formData.email) newErrors.email = 'Email is required';
      else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';

      // Only validate password once email is valid (progressive reveal)
      if (isEmailValid()) {
        if (!formData.password) {
          newErrors.password = 'Password is required';
        } else if (!isPasswordValid()) {
          newErrors.password = 'Password must include one uppercase letter, one number, and one special character';
        }
      }

      // Only validate confirm password once password meets strength requirements
      if (isEmailValid() && isPasswordValid()) {
        if (!formData.confirmPassword) newErrors.confirmPassword = 'Confirm password is required';
        else if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const handleNext = async () => {
      if (isCheckingEmail) return;

      const emailValidNow = isEmailValid();
      const passwordValidNow = isPasswordValid();

      // Ensure inline errors state is updated
      const isValid = validate();
      if (!isValid) {
        // Toast based on what's currently visible/progressively revealed
        if (!formData.email) {
          toast.error('Email is required');
          return;
        }
        if (!emailValidNow) {
          toast.error('Email is invalid');
          return;
        }
        if (emailValidNow && !formData.password) {
          toast.error('Password is required');
          return;
        }
        if (emailValidNow && formData.password && !passwordValidNow) {
          toast.error('Password must include one uppercase letter, one number, and one special character');
          return;
        }
        if (emailValidNow && passwordValidNow && !formData.confirmPassword) {
          toast.error('Confirm password is required');
          return;
        }
        if (emailValidNow && passwordValidNow && formData.confirmPassword && formData.password !== formData.confirmPassword) {
          toast.error('Passwords do not match. Please try again.');
          return;
        }

        // Fallback (should be rare, but keeps UX consistent)
        const firstError = Object.values(errors)[0];
        if (firstError) toast.error(firstError);
        return;
      }

      // For normal signup, check if email already exists before moving to phone step
      try {
        setIsCheckingEmail(true);
        const result = await AuthService.checkExistence({ email: formData.email });
        if (result.email === true) {
          setErrors((prev) => ({
            ...prev,
            email: 'This email is already registered. Please sign in instead.',
          }));
          toast.error('This email is already registered with another account. Please sign in instead.');
          return;
        }
        onNext();
      } catch (error) {
        toast.error(getAuthErrorMessage(error, 'Unable to verify email. Please try again.'));
      } finally {
        setIsCheckingEmail(false);
      }
    };

    // Check if password is valid (meets all requirements)
    const isPasswordValid = () => {
      if (!formData.password) return false;
      const hasUpperCase = /[A-Z]/.test(formData.password);
      const hasNumber = /[0-9]/.test(formData.password);
      const hasSpecialChar = /[^A-Za-z0-9]/.test(formData.password);
      return hasUpperCase && hasNumber && hasSpecialChar;
    };

    const passwordValid = isPasswordValid();
    const emailValid = isEmailValid();

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleNext();
      }
    };

    const isNativeIOS = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';

    return (
      <AuthLayout showImage={true}>
        <form
          className="px-4 pt-4 pb-[calc(env(safe-area-inset-bottom,0px)+1rem)] lg:px-0 lg:pt-0 lg:pb-0"
          onKeyDown={handleKeyDown}
          onSubmit={(e) => { e.preventDefault(); void handleNext(); }}
        >
          {/* <Logo size="md" className="mb-4" /> */}
          <h1 className="relative z-[999] text-3xl lg:text-4xl font-bold bg-gradient-to-b from-white to-gray-400 bg-clip-text text-transparent tracking-wide mb-1">
            Shine Among The Stars
          </h1>
          <p className="text-sm text-[#8E8E93] tracking-wide mb-10">By creating your artist profile.</p>

          <div className="space-y-2">
            <Input
              ref={emailRef}
              label="Email address"
              type="email"
              placeholder="Enter your email"
              value={formData.email}
              onChange={(e) => {
                updateFormData({ email: e.target.value });
                if (errors.email) setErrors((prev) => ({ ...prev, email: '' }));
              }}
              error={errors.email}
              className="bg-black/90"
            />
            <div
              style={{
                maxHeight: emailValid ? '220px' : '0',
                opacity: emailValid ? 1 : 0,
                transform: emailValid ? 'translateY(0) translateZ(0)' : 'translateY(-8px) translateZ(0)',
                overflow: emailValid ? 'visible' : 'hidden',
                paddingTop: emailValid ? '0.5rem' : '0',
                paddingBottom: emailValid ? '0.5rem' : '0',
                transition:
                  'max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1), transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), padding-top 0.4s cubic-bezier(0.4, 0, 0.2, 1), padding-bottom 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                WebkitTransform: emailValid ? 'translateY(0) translateZ(0)' : 'translateY(-8px) translateZ(0)',
                WebkitBackfaceVisibility: 'hidden',
                WebkitPerspective: '1000px',
                backfaceVisibility: 'hidden',
                willChange: 'max-height, opacity, transform',
                pointerEvents: emailValid ? 'auto' : 'none',
              }}
            >
              <Input
                ref={passwordRef}
                label="Password"
                type="password"
                placeholder="Create a password"
                value={formData.password}
                onChange={(e) => {
                  updateFormData({ password: e.target.value });
                  if (errors.password) setErrors((prev) => ({ ...prev, password: '' }));
                }}
                error={errors.password}
                showPasswordToggle
                showPasswordStrength
                className="bg-black/90"
              />
            </div>
            <div
              style={{
                maxHeight: emailValid && passwordValid ? '200px' : '0',
                opacity: emailValid && passwordValid ? 1 : 0,
                transform: emailValid && passwordValid ? 'translateY(0) translateZ(0)' : 'translateY(-8px) translateZ(0)',
                overflow: emailValid && passwordValid ? 'visible' : 'hidden',
                paddingTop: emailValid && passwordValid ? '0.5rem' : '0',
                paddingBottom: emailValid && passwordValid ? '0.5rem' : '0',
                transition: 'max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1), transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), padding-top 0.4s cubic-bezier(0.4, 0, 0.2, 1), padding-bottom 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                // iOS Safari optimizations for smooth animations
                WebkitTransform: emailValid && passwordValid ? 'translateY(0) translateZ(0)' : 'translateY(-8px) translateZ(0)',
                WebkitBackfaceVisibility: 'hidden',
                WebkitPerspective: '1000px',
                backfaceVisibility: 'hidden',
                willChange: 'max-height, opacity, transform',
                pointerEvents: emailValid && passwordValid ? 'auto' : 'none',
              }}
            >
              <Input
                ref={confirmPasswordRef}
                label="Confirm password"
                type="password"
                placeholder="Re-enter your password"
                value={formData.confirmPassword}
                onChange={(e) => {
                  updateFormData({ confirmPassword: e.target.value });
                  if (errors.confirmPassword) setErrors((prev) => ({ ...prev, confirmPassword: '' }));
                }}
                error={errors.confirmPassword}
                showPasswordToggle
                className="bg-black/90"
              />
            </div>
          </div>

          <Button type="submit" variant="active" className="w-full my-5 uppercase tracking-wider" isLoading={isCheckingEmail}>
            Sign up
          </Button>

          <div className="text-center">
            <p className="text-sm text-[#8E8E93]">
              Already have an account?{' '}
              <Link
                to="/login"
                className="font-semibold text-[#F33363] hover:text-[#D92D58] transition-colors"
              >
                Sign in
              </Link>
            </p>
          </div>

          <div className="text-center">
            <p className="text-sm text-[#8E8E93]">or sign up using</p>
            <div className="mt-4 flex justify-center gap-4 items-center">
              {canUseTruecaller() && (
                <button
                  type="button"
                  className="flex h-12 w-12 items-center justify-center rounded-lg transition-colors"
                  aria-label="Sign up with Truecaller"
                  onClick={onTruecallerClick}
                  disabled={isSsoLoading}
                >
                  <img src={TrueCallerIcon} alt="Truecaller" className="h-10 w-10" />
                </button>
              )}
              {Capacitor.isNativePlatform() ? (
                <button
                  type="button"
                  className={cn(
                    "flex h-12 w-12 items-center justify-center rounded-full bg-white hover:bg-gray-100 transition-colors",
                    isSsoLoading && "opacity-60 pointer-events-none",
                  )}
                  aria-label="Sign up with Google"
                  onClick={() => {
                    if (isSsoLoading) return;
                    onNativeGoogleClick?.();
                  }}
                  disabled={isSsoLoading}
                >
                  <svg className="h-6 w-6" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                </button>
              ) : (
                onGoogleSuccess ? (
                  <div
                    className={cn(isSsoLoading && 'opacity-60 pointer-events-none')}
                    onMouseDown={() => {
                      if (!isSsoLoading) onSsoStart?.();
                    }}
                    onTouchStart={() => {
                      if (!isSsoLoading) onSsoStart?.();
                    }}
                  >
                    <GoogleLogin
                      onSuccess={onGoogleSuccess}
                      onError={() => toast.error('Google sign-up was cancelled or failed.')}
                      theme="filled_black"
                      size="large"
                      type="icon"
                      shape="circle"
                      useOneTap={false}
                    />
                  </div>
                ) : (
                  <button
                    type="button"
                    className="flex h-12 w-12 items-center justify-center rounded-lg bg-white hover:bg-gray-100 transition-colors"
                    aria-label="Sign up with Google"
                  >
                    <svg className="h-6 w-6" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                  </button>
                )
              )}
              {isNativeIOS && (
                <button
                  type="button"
                  className={cn(
                    "flex h-12 w-12 items-center justify-center rounded-full bg-black border border-white/20 hover:bg-neutral-900 transition-colors",
                    isSsoLoading && "opacity-60 pointer-events-none",
                  )}
                  aria-label="Sign up with Apple"
                  onClick={() => {
                    if (isSsoLoading) return;
                    onAppleClick?.();
                  }}
                  disabled={isSsoLoading}
                >
                  <svg className="h-6 w-6 text-white" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                  </svg>
                </button>
              )}
              <button
                type="button"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 hover:opacity-90 transition-opacity disabled:opacity-60"
                aria-label="Sign up with Instagram"
                onClick={() => {
                  if (isSsoLoading) return;
                  onInstagramClick?.();
                }}
                disabled={isSsoLoading}
              >
                <svg className="h-6 w-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </button>
            </div>
          </div>
        </form>
      </AuthLayout>
    );
  };

// Step 2: Verify your phone number
const Step2: React.FC<{
  formData: SignupFormData;
  updateFormData: (data: Partial<SignupFormData>) => void;
  onNext: () => void;
  onBack?: () => void;
}> = ({ formData, updateFormData, onNext }) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSendingOTP, setIsSendingOTP] = useState(false);
  const [isCheckingPhone, setIsCheckingPhone] = useState(false);
  const [isPhoneAvailable, setIsPhoneAvailable] = useState<boolean | null>(null);
  const [lastCheckedPhone, setLastCheckedPhone] = useState<string | null>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  const prevPhoneRef = useRef<string>('');
  const isFirstRenderRef = useRef(true);

  // Auto-focus phone input when step loads
  useEffect(() => {
    if (phoneRef.current) {
      setTimeout(() => {
        phoneRef.current?.focus();
        // Position cursor at end of text if there's existing value
        if (phoneRef.current && phoneRef.current.setSelectionRange && formData.phoneNumber) {
          const length = phoneRef.current.value.length;
          phoneRef.current.setSelectionRange(length, length);
        }
      }, 0);
    }
  }, []); // Only run on mount

  // Focus error field
  useEffect(() => {
    if (errors.phoneNumber && phoneRef.current) {
      setTimeout(() => {
        phoneRef.current?.focus();
        // Position cursor at end of text
        if (phoneRef.current && phoneRef.current.setSelectionRange) {
          const length = phoneRef.current.value.length;
          phoneRef.current.setSelectionRange(length, length);
        }
      }, 0);
    }
  }, [errors]);

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.phoneNumber) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!isValidIndianPhoneNumber(formData.phoneNumber)) {
      newErrors.phoneNumber = INDIAN_PHONE_ERROR_MESSAGE;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const sendOtp = async () => {
    if (isSendingOTP || isCheckingPhone) return;
    if (!validate()) {
      if (formData.phoneNumber && !isValidIndianPhoneNumber(formData.phoneNumber)) {
        toast.error(INDIAN_PHONE_ERROR_MESSAGE);
      }
      return;
    }

    setIsSendingOTP(true);
    setErrors({});
    try {
      const phone = formData.phoneNumber;

      // If we already know this exact phone is available from a recent check
      // and auto-OTP is allowed, skip re-checking existence.
      let available = isPhoneAvailable;
      if (!(available === true && lastCheckedPhone === phone && !formData.disableAutoPhoneOtp)) {
        const result = await AuthService.checkExistence({ phone });
        const exists = result.phone === true;
        available = !exists;
        setIsPhoneAvailable(available);
        setLastCheckedPhone(phone);
        if (exists) {
          setErrors({
            phoneNumber: 'This phone number is already registered. Please use a different number.',
          });
          toast.error('This phone number is already registered with another account. Please use a different number.');
          return;
        }
      }

      await AuthService.signupSendOtp(phone);
      toast.success('OTP sent successfully');
      onNext();
    } catch (error) {
      const message = getAuthErrorMessage(error, 'Failed to send OTP. Please try again.');
      toast.error(message);
    } finally {
      setIsSendingOTP(false);
    }
  };

  useEffect(() => {
    const phone = formData.phoneNumber;
    const prevPhone = prevPhoneRef.current;

    // On first render of this step, never auto-fire existence or OTP,
    // even if 10 digits are already present (e.g. coming back from OTP step).
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      prevPhoneRef.current = phone;
      if (!phone || phone.length < 10) {
        setIsPhoneAvailable(null);
        setLastCheckedPhone(null);
      }
      return;
    }

    // If phone is cleared or reduced below 10 digits, reset availability
    // and wait until it reaches 10 again.
    if (!phone || phone.length < 10) {
      setIsPhoneAvailable(null);
      setLastCheckedPhone(null);
      prevPhoneRef.current = phone;
      return;
    }

    // Only auto-check when user *crosses* from <10 digits to exactly 10 digits.
    const prevLength = prevPhone ? prevPhone.length : 0;
    if (prevLength >= 10 || phone.length !== 10) {
      prevPhoneRef.current = phone;
      return;
    }

    // If user has explicitly navigated back from OTP to change phone number,
    // do not auto-fire existence/OTP until they modify the input (which
    // clears disableAutoPhoneOtp in onChange).
    if (formData.disableAutoPhoneOtp || isCheckingPhone) {
      prevPhoneRef.current = phone;
      return;
    }

    const checkPhone = async (value: string) => {
      try {
        setIsCheckingPhone(true);
        const result = await AuthService.checkExistence({ phone: value });
        const exists = result.phone === true;
        setIsPhoneAvailable(!exists);
        setLastCheckedPhone(value);
        if (exists) {
          setErrors((prev) => ({
            ...prev,
            phoneNumber: 'This phone number is already registered. Please use a different number.',
          }));
          toast.error('This phone number is already registered with another account. Please use a different number.');
        } else {
          setErrors((prev) => {
            const { phoneNumber, ...rest } = prev;
            return rest;
          });
          // Phone does not exist and is valid; automatically send OTP and move to next step
          void sendOtp();
        }
      } catch (error) {
        toast.error(getAuthErrorMessage(error, 'Unable to verify phone number. Please try again.'));
      } finally {
        setIsCheckingPhone(false);
      }
    };

    prevPhoneRef.current = phone;
    void checkPhone(phone);
  }, [formData.phoneNumber, formData.disableAutoPhoneOtp, isCheckingPhone]);

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    await sendOtp();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void handleSendOTP(e as unknown as React.FormEvent);
    }
  };

  return (
    <AuthLayout showImage={false} backgroundVariant="stars">
      <form className=" space-y-6 px-4 py-8 lg:px-0" onKeyDown={handleKeyDown} onSubmit={handleSendOTP}>
        {/* <Logo size="md" className="mb-4" /> */}
        <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-b from-white to-gray-400 bg-clip-text text-transparent capitalize tracking-wide">Verify your phone number</h1>
        <p className="text-sm text-[#8E8E93] tracking-wide">We'll send you a one-time password to verify your account.</p>

        <PhoneInput
          ref={phoneRef}
          label="Phone number"
          value={formData.phoneNumber}
          onChange={(value) =>
            // As soon as user edits the phone after coming back from OTP,
            // re-enable existence checks/auto-OTP by clearing the flag.
            updateFormData({ phoneNumber: value, disableAutoPhoneOtp: false })
          }
          error={errors.phoneNumber}
          isLoading={isCheckingPhone}
        />

        <Button
          type="submit"
          variant={isPhoneAvailable === false ? 'disabled' : 'active'}
          className="w-full"
          disabled={isSendingOTP || isCheckingPhone || isPhoneAvailable === false}
          isLoading={isSendingOTP}
        >
          Send OTP
        </Button>
      </form>
    </AuthLayout>
  );
};

// Step 3: Enter verification code
const Step3: React.FC<{
  formData: SignupFormData;
  updateFormData: (data: Partial<SignupFormData>) => void;
  onNext: () => void;
  onBack: () => void;
}> = ({ formData, updateFormData, onNext, onBack }) => {
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResendingOTP, setIsResendingOTP] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);

  const handleOTPComplete = async (otp: string) => {
    updateFormData({ otp });
    setOtpError(null);
    setIsVerifying(true);
    try {
      await AuthService.signupVerifyOtp(formData.phoneNumber, otp);

      // Persist progress immediately after successful OTP verification so any
      // auth-based redirects (e.g. from PublicRouteGuard) always send the user
      // back into the onboarding flow (step 4) instead of dropping them onto
      // user-home with an incomplete profile.
      const earlyProgress: SignupProgress = {
        step: 4,
        source: formData.source ?? 'normal',
        email: formData.email,
        fullName: formData.fullName,
        phoneNumber: formData.phoneNumber,
      };
      try {
        await storageManager.set(STORAGE_KEYS.SIGNUP_PROGRESS, earlyProgress);
      } catch (err) {
        console.error('Failed to persist signup progress right after OTP verification:', err);
      }

      const signupResponse = await AuthService.signupRegister({
        email: formData.email,
        password: formData.password,
        confirm_password: formData.confirmPassword,
        phone: formData.phoneNumber,
      });

      // Update source if returned from signup
      if (signupResponse && (signupResponse as any).source) {
        updateFormData({ source: (signupResponse as any).source });
      }

      // Fetch categories after successful signup
      const categories = await AuthService.getCategories();
      updateFormData({ fetchedCategories: categories });

      // Persist progress so user can resume from category step if they drop off
      const progress: SignupProgress = {
        step: 4,
        source: (signupResponse as any)?.source ?? formData.source ?? 'normal',
        email: formData.email,
        fullName: formData.fullName,
        phoneNumber: formData.phoneNumber,
      };
      try {
        await storageManager.set(STORAGE_KEYS.SIGNUP_PROGRESS, progress);
      } catch (err) {
        console.error('Failed to persist signup progress after OTP verification:', err);
      }

      onNext();
    } catch (error) {
      const message = getAuthErrorMessage(error, 'Verification failed. Please check the code and try again.');
      setOtpError(message);
      toast.error(message);
    } finally {
      setIsVerifying(false);
    }
  };



  const formatPhoneDisplay = (phone: string) => {
    if (!phone || phone.length < 3) return phone;
    const masked = phone.slice(-3).padStart(10, 'X');
    return `${masked.slice(0, 5)} ${masked.slice(5, 10)}`;
  };

  const handleResendOtp = async () => {
    if (isVerifying || isResendingOTP) return;
    if (!isValidIndianPhoneNumber(formData.phoneNumber || '')) {
      toast.error(INDIAN_PHONE_ERROR_MESSAGE);
      return;
    }

    try {
      setIsResendingOTP(true);
      await AuthService.signupSendOtp(formData.phoneNumber);
      updateFormData({ otp: '' });
      setOtpError(null);
      toast.success('OTP sent successfully');
    } catch (error) {
      toast.error(getAuthErrorMessage(error, 'Failed to resend OTP. Please try again.'));
    } finally {
      setIsResendingOTP(false);
    }
  };

  return (
    <AuthLayout showImage={false} backgroundVariant="stars">
      <div className="space-y-6 px-4 py-8 lg:px-0">
        {/* <Logo size="md" className="mb-4" /> */}
        <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-b from-white to-gray-400 bg-clip-text text-transparent capitalize tracking-wide">Enter verification code</h1>
        <p className="text-sm text-[#8E8E93] tracking-wide">
          We've sent a 4-digit code to +91 {formatPhoneDisplay(formData.phoneNumber || '')}
        </p>

        {isVerifying && (
          <div className="flex justify-center py-2">
            <Loader size="small" />
          </div>
        )}
        <OTPInput
          length={4}
          value={formData.otp}
          onChange={(value) => { updateFormData({ otp: value }); setOtpError(null); }}
          onComplete={handleOTPComplete}
          autoFocus={true}
        />
        {otpError && (
          <p className="text-xs text-destructive text-center">{otpError}</p>
        )}

        <div className="space-y-2 text-center">
          <p className="text-sm text-[#8E8E93]">
            Didn't receive the code?{' '}
            <button
              type="button"
              className="font-semibold text-[#F33363] disabled:opacity-60"
              onClick={() => {
                void handleResendOtp();
              }}
              disabled={isVerifying || isResendingOTP}
            >
              {isResendingOTP ? 'Resending...' : 'Resend OTP'}
            </button>
          </p>
          <button
            type="button"
            onClick={() => {
              // When user goes back to change phone number, disable automatic OTP send on existence check
              updateFormData({ disableAutoPhoneOtp: true });
              onBack();
            }}
            className="text-sm text-[#8E8E93] hover:text-white transition-colors"
            disabled={isVerifying}
          >
            Change phone number
          </button>
        </div>
      </div>
    </AuthLayout>
  );
};

// Step 4: Choose your category
const Step4: React.FC<{
  formData: SignupFormData;
  updateFormData: (data: Partial<SignupFormData>) => void;
  onNext: () => void;
  onBack?: () => void;
}> = ({ formData, updateFormData, onNext }) => {
  const [loadingCategorySlug, setLoadingCategorySlug] = useState<string | null>(null);

  // Format category name as requested: capitalize words and space out slashes
  const formatCategoryName = (name: string) => {
    return name
      .split('/')
      .map((part) =>
        part
          .trim()
          .toLowerCase()
          .split(' ')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      )
      .join(' / ');
  };

  const categoriesToDisplay = formData.fetchedCategories && formData.fetchedCategories.length > 0
    ? formData.fetchedCategories
    : [
      { id: 1, name: 'CELEBRITY APPEARANCE', category_banner_image: '', value: 'celebrity', slug: 'celebrity' },
      { id: 2, name: 'ANCHOR/EMCEE', category_banner_image: '', value: 'anchor', slug: 'anchor-emcee' },
      { id: 3, name: 'DJ', category_banner_image: '', value: 'dj', slug: 'dj' },
      { id: 4, name: 'DANCER/TROUPE', category_banner_image: '', value: 'dancer', slug: 'dancer-troupe' },
      { id: 5, name: 'COMEDIAN', category_banner_image: '', value: 'comedian', slug: 'comedian' },
      { id: 6, name: 'MAGICIAN', category_banner_image: '', value: 'magician', slug: 'magician' },
    ];

  // Handle category selection with API call and auto-navigation
  const handleCategorySelect = async (category: any) => {
    if (loadingCategorySlug) return;

    setLoadingCategorySlug(category.slug);

    try {
      // Fetch subcategories (genres)
      const subcategories = await AuthService.getSubcategories(category.slug);

      // Update form data with both selected category and fetched subcategories
      updateFormData({
        category: category.slug,
        genres: [], // reset genres if category changed
        fetchedSubcategories: subcategories
      });

      // Navigate to next step
      onNext();
    } catch (error) {
      console.error('Failed to fetch subcategories:', error);
      toast.error('Failed to load genres. Please try again.');
      // Keep user on this step when subcategories cannot be loaded
      updateFormData({ category: category.slug });
    } finally {
      setLoadingCategorySlug(null);
    }
  };

  return (
    <AuthLayout showImage={false} fullWidth={true} backgroundVariant="stars">
      <div className="space-y-6 px-4 py-8 lg:px-40">
        {/* <Logo size="md" className="mb-4" /> */}
        <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-b from-white to-gray-400 bg-clip-text text-transparent capitalize tracking-wide">Choose your category</h1>
        <p className="text-sm text-[#8E8E93] tracking-wide">Select the category that best describes you</p>

        <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 md:gap-4 lg:grid-cols-5 lg:gap-4 max-h-[60vh] overflow-y-auto overflow-x-hidden overscroll-contain">
          {categoriesToDisplay.map((category) => (
            <button
              key={category.id}
              type="button"
              onClick={() => handleCategorySelect(category)}
              disabled={!!loadingCategorySlug}
              className={`group flex flex-col items-center space-y-1.5 transition-opacity duration-300 ${loadingCategorySlug && loadingCategorySlug !== category.slug ? 'opacity-50 grayscale-[0.5]' : 'opacity-100'}`}
            >
              <div
                className={`relative h-16 w-16 overflow-hidden rounded-full border-2 transition-all duration-300 sm:h-20 sm:w-20 md:h-24 md:w-24 ${formData.category === category.slug
                  ? 'border-[#F33363] ring-2 ring-[#F33363]/20 scale-105'
                  : 'border-[#2C2C2E] bg-[#2C2C2E] group-hover:border-[#F33363]/50'
                  }`}
              >
                {category.category_banner_image ? (
                  <img
                    src={category.category_banner_image}
                    alt={category.name}
                    className={`h-full w-full object-cover transition-all duration-500 rounded-full group-hover:scale-110 ${loadingCategorySlug === category.slug ? 'blur-sm scale-95 opacity-50' : ''}`}
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-[#1C1C1E]">
                    <span className="text-xs text-[#48484A]">No Image</span>
                  </div>
                )}

                {/* Loader for the clicked category */}
                {loadingCategorySlug === category.slug && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <Loader size="small" />
                  </div>
                )}

                {/* Overlay for selected state */}
                {formData.category === category.slug && !loadingCategorySlug && (
                  <div className="absolute inset-0 bg-[#F33363]/10" />
                )}
              </div>
              <span className={`text-center text-xs font-medium transition-colors duration-200 sm:text-sm ${formData.category === category.slug ? 'text-white' : 'text-[#8E8E93] group-hover:text-[#AEAEB2]'
                }`}>
                {formatCategoryName(category.name)}
              </span>
            </button>
          ))}
        </div>

        <div className="mt-8 flex justify-center">
          <Button
            variant={formData.category ? 'active' : 'disabled'}
            className="w-full max-w-md"
            onClick={onNext}
            disabled={!formData.category}
          >
            Next
          </Button>
        </div>
      </div>
    </AuthLayout>
  );
};

// Step 5: Choose your genres
const Step5: React.FC<{
  formData: SignupFormData;
  updateFormData: (data: Partial<SignupFormData>) => void;
  onNext: () => void;
  onBack?: () => void;
}> = ({ formData, updateFormData, onNext, onBack }) => {
  const isInfluencerCategorySelected = useMemo(() => {
    if (!formData.category) return false;
    const categories = formData.fetchedCategories || [];
    const selected = categories.find((c) => c.slug === formData.category);
    if (!selected) return false;
    const slug = (selected.slug || '').toLowerCase();
    const name = (selected.name || '').toLowerCase();
    return slug.includes('influencer') || name.includes('influencer');
  }, [formData.category, formData.fetchedCategories]);

  const genres = formData.fetchedSubcategories && formData.fetchedSubcategories.length > 0
    ? formData.fetchedSubcategories.map(sub => ({
      value: sub.slug,
      label: sub.name
    }))
    : [
      { value: 'acoustic', label: 'Acoustic Singer' },
      { value: 'label', label: 'Label' },
      { value: 'bhajan', label: 'Bhajan' },
      { value: 'english-retro', label: 'English Retro' },
      { value: 'ghazal', label: 'Ghazal' },
      { value: 'hindi-retro', label: 'Hindi Retro' },
      { value: 'indian-classical', label: 'Indian Classical' },
      { value: 'indian-folk', label: 'Indian Folk' },
      { value: 'jagran', label: 'Jagran' },
      { value: 'karaoke', label: 'Karaoke' },
      { value: 'qawwali', label: 'Qawwali' },
      { value: 'rapper', label: 'Rapper' },
      { value: 'religious', label: 'Religious' },
    ];

  return (
    <AuthLayout showImage={false} fullWidth={true} backgroundVariant="stars">
      <div className="space-y-6 px-4 py-8 lg:px-40 h-screen">
        {/* <Logo size="md" className="mb-4" /> */}
        <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-b from-white to-gray-400 bg-clip-text text-transparent capitalize tracking-wide">Choose your genres</h1>
        <p className="text-sm text-[#8E8E93] tracking-wide">Select one or more genres that represent your performance</p>

        <MultiSelect
          options={genres}
          value={formData.genres || []}
          onChange={(value) => {
            const next =
              isInfluencerCategorySelected && value.length > 0
                ? [value[value.length - 1]]
                : value;
            updateFormData({ genres: next });
          }}
        />

        <div className="flex justify-center gap-3">
          <Button
            type="button"
            className="w-full max-w-md border border-[#3A3A3C] bg-transparent text-white hover:border-[#F33363]/50"
            onClick={onBack}
          >
            Back
          </Button>
          <Button
            variant={formData.genres && formData.genres.length > 0 ? 'active' : 'disabled'}
            className="w-full max-w-md"
            onClick={onNext}
            disabled={!formData.genres || formData.genres.length === 0}
          >
            Next
          </Button>
        </div>
      </div>
    </AuthLayout>
  );
};

// Step 6: Complete your profile
const Step6: React.FC<{
  formData: SignupFormData;
  updateFormData: (data: Partial<SignupFormData>) => void;
  onSubmit: () => void;
  isLoading: boolean;
  onBack?: () => void;
}> = ({ formData, updateFormData, onSubmit, isLoading, onBack }) => {
  const navigate = useNavigate();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isCheckingGooglePhone, setIsCheckingGooglePhone] = useState(false);
  const [isGooglePhoneAvailable, setIsGooglePhoneAvailable] = useState<boolean | null>(null);
  const [lastCheckedGooglePhone, setLastCheckedGooglePhone] = useState<string | null>(null);

  // Refs for focusing
  const stageNameRef = useRef<HTMLInputElement>(null);
  const stageNameContainerRef = useRef<HTMLDivElement>(null);
  const fullNameRef = useRef<HTMLInputElement>(null);
  const agencyNameRef = useRef<HTMLInputElement>(null);
  const relationRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);
  const additionalEmailRef = useRef<HTMLInputElement>(null);

  // Similar profiles state
  const [similarProfiles, setSimilarProfiles] = useState<SimilarProfile[]>([]);
  const [similarProfilesCount, setSimilarProfilesCount] = useState<number | null>(null);
  const [similarProfilesPage, setSimilarProfilesPage] = useState(1);
  const [similarProfilesPageSize, setSimilarProfilesPageSize] = useState<number | null>(null);
  const [hasSimilarProfilesLoaded, setHasSimilarProfilesLoaded] = useState(false);
  const [isCheckingProfiles, setIsCheckingProfiles] = useState(false);
  const [showSimilarProfiles, setShowSimilarProfiles] = useState(true);
  const [similarProfilesSheetOpen, setSimilarProfilesSheetOpen] = useState(false);
  const [resetPasswordProfile, setResetPasswordProfile] = useState<SimilarProfile | null>(null);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [appleFieldEditing, setAppleFieldEditing] = useState<{ fullName: boolean; email: boolean }>({
    fullName: false,
    email: false,
  });

  const isApplePrefilledField = (field: 'fullName' | 'email', value: string) =>
    formData.source === 'apple' && Boolean(value.trim()) && !appleFieldEditing[field];

  const renderAppleEditAction = (field: 'fullName' | 'email') => (
    <button
      type="button"
      aria-label={`Edit ${field === 'fullName' ? 'full name' : 'email'}`}
      className="flex items-center justify-center text-[#8E8E93] hover:text-white transition-colors"
      onClick={() => {
        setAppleFieldEditing((prev) => ({ ...prev, [field]: true }));
        window.setTimeout(() => {
          if (field === 'fullName') fullNameRef.current?.focus();
          else emailRef.current?.focus();
        }, 0);
      }}
    >
      <Pencil className="h-4 w-4" />
    </button>
  );

  const profileManagedByOptions = [
    { value: 'self', label: 'Self' },
    { value: 'manager', label: 'Manager' },
    { value: 'agency', label: 'Agency' },
    { value: 'family', label: 'Family' },
  ];

  // Configuration for fields based on source (phone for Google shown above Profile Managed By)
  const fieldConfig = {
    normal: ['stageName', 'fullName', 'profileManagedBy'],
    google: ['stageName', 'fullName', 'phoneNumber', 'profileManagedBy'],
    truecaller: ['stageName', 'fullName', 'profileManagedBy', 'email'],
    instagram: ['stageName', 'fullName', 'profileManagedBy', 'phone', 'email'],
    apple: ['stageName', 'fullName', 'profileManagedBy', 'phone', 'email'],
  };

  const activeFields = fieldConfig[formData.source] || fieldConfig.normal;

  // Debounced search for similar profiles via API with pagination
  const searchSimilarProfiles = async (stageName: string, page = 1) => {
    const trimmed = stageName.trim();
    if (trimmed.length < 3) {
      setSimilarProfiles([]);
      setSimilarProfilesCount(null);
      setSimilarProfilesPage(1);
      setSimilarProfilesPageSize(null);
      setHasSimilarProfilesLoaded(false);
      return;
    }

    setIsCheckingProfiles(true);
    setShowSimilarProfiles(true);

    try {
      const res = await AuthService.getSimilarProfiles(trimmed, page);
      const profiles: SimilarProfile[] = res.items.map((p) => ({
        id: String(p.id),
        slug: p.slug,
        stageName: p.professional_name,
        profileImage: p.profile_pic,
        masked_email: p.masked_email,
        masked_phone: p.masked_phone,
      }));
      setSimilarProfiles(profiles);
      setSimilarProfilesCount(res.count);
      setSimilarProfilesPage(page);
      setSimilarProfilesPageSize(res.pageSize || profiles.length || null);
      setHasSimilarProfilesLoaded(true);
    } catch (error) {
      console.error('Failed to search similar profiles:', error);
      setSimilarProfiles([]);
      setHasSimilarProfilesLoaded(false);
    } finally {
      setIsCheckingProfiles(false);
    }
  };

  // Close similar profiles dropdown when clicking outside the stage name + dropdown container
  useEffect(() => {
    if (!showSimilarProfiles) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      // If the sheet is open, ignore outside clicks; otherwise closing the sheet
      // would also hide the "Found X profiles" teaser.
      if (similarProfilesSheetOpen) return;
      const container = stageNameContainerRef.current;
      if (!container) return;
      const target = event.target as Node | null;
      if (target && !container.contains(target)) {
        setShowSimilarProfiles(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showSimilarProfiles, similarProfilesSheetOpen]);

  const handleStageNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    updateFormData({ stageName: value });
    setSimilarProfilesPage(1);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => { void searchSimilarProfiles(value, 1); }, 500);
  };

  useEffect(() => {
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, []);

  // For Google signup, validate phone number existence on 10 digits
  useEffect(() => {
    if (formData.source !== 'google') {
      setIsGooglePhoneAvailable(null);
      setLastCheckedGooglePhone(null);
      setIsCheckingGooglePhone(false);
      return;
    }

    const phone = formData.phoneNumber;
    if (!phone || phone.length < 10) {
      setIsGooglePhoneAvailable(null);
      setLastCheckedGooglePhone(null);
      return;
    }
    if (phone === lastCheckedGooglePhone || isCheckingGooglePhone) return;

    const checkPhone = async (value: string) => {
      try {
        setIsCheckingGooglePhone(true);
        const result = await AuthService.checkExistence({ phone: value });
        const exists = result.phone === true;
        setIsGooglePhoneAvailable(!exists);
        setLastCheckedGooglePhone(value);
        if (exists) {
          setErrors((prev) => ({
            ...prev,
            phoneNumber: 'This phone number is already registered. Please use a different number.',
          }));
          toast.error('This phone number is already registered with another account. Please use a different number.');
        } else {
          setErrors((prev) => {
            const { phoneNumber, ...rest } = prev;
            return rest;
          });
        }
      } catch (error) {
        toast.error(getAuthErrorMessage(error, 'Unable to verify phone number. Please try again.'));
      } finally {
        setIsCheckingGooglePhone(false);
      }
    };

    void checkPhone(phone);
  }, [formData.source, formData.phoneNumber, isCheckingGooglePhone, lastCheckedGooglePhone]);

  const handleClaimProfile = (profile: SimilarProfile) => navigate('/claim-profile', { state: { profile } });
  const handleResetPassword = (profile: SimilarProfile) => { setResetPasswordProfile(profile); setIsResetModalOpen(true); };
  const handleResetPasswordSuccess = () => {
    setIsResetModalOpen(false);
    setResetPasswordProfile(null);
    toast.success('Password reset successfully. You can sign in now.');
    navigate('/login');
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    // Base fields
    if (!formData.stageName) newErrors.stageName = 'Stage name is required';
    else if (formData.stageName.trim().length < 3) newErrors.stageName = 'Stage name must be at least 3 characters';

    if (!formData.fullName) newErrors.fullName = 'Full name is required';
    if (!formData.profileManagedBy) newErrors.profileManagedBy = 'Please select an option';

    // Conditional Managed By fields
    if (formData.profileManagedBy === 'agency' && !formData.agencyName) newErrors.agencyName = 'Agency name is required';
    if (formData.profileManagedBy === 'family' && !formData.relation) newErrors.relation = 'Relation is required';

    // Source dependent fields
    if (activeFields.includes('phoneNumber') && !formData.phoneNumber) newErrors.phoneNumber = 'Phone number is required';
    if (activeFields.includes('phone') && !formData.phone) newErrors.phone = 'Phone number is required';
    if (activeFields.includes('email') && !formData.email) newErrors.email = 'Email is required';
    if (activeFields.includes('password') && !formData.password) newErrors.password = 'Password is required';
    if (activeFields.includes('confirmPassword')) {
      if (!formData.confirmPassword) newErrors.confirmPassword = 'Confirm password is required';
      else if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    }

    // Format validations (especially for Truecaller/Instagram sources)
    if (activeFields.includes('phoneNumber') && formData.phoneNumber && !isValidIndianPhoneNumber(formData.phoneNumber)) {
      newErrors.phoneNumber = INDIAN_PHONE_ERROR_MESSAGE;
    }
    if (activeFields.includes('phone') && formData.phone) {
      const digits = String(formData.phone).replace(/\D/g, '');
      if (!isValidIndianPhoneNumber(digits)) newErrors.phone = INDIAN_PHONE_ERROR_MESSAGE;
    }
    if (activeFields.includes('email') && formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (formData.source === 'google' && activeFields.includes('phoneNumber') && formData.phoneNumber && formData.phoneNumber.length === 10) {
      if (isGooglePhoneAvailable === false) {
        newErrors.phoneNumber = 'This phone number is already registered. Please use a different number.';
      } else if (isCheckingGooglePhone) {
        newErrors.phoneNumber = 'Please wait while we verify your phone number.';
      }
    }

    setErrors(newErrors);
    return newErrors;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length === 0) {
      onSubmit();
      return;
    }

    const firstKey = Object.keys(newErrors)[0];
    const message = newErrors[firstKey];
    if (message) toast.error(message);

    if (firstKey === 'stageName') stageNameRef.current?.focus();
    else if (firstKey === 'fullName') fullNameRef.current?.focus();
    else if (firstKey === 'phoneNumber') phoneRef.current?.focus();
    else if (firstKey === 'phone') phoneRef.current?.focus();
    else if (firstKey === 'email') emailRef.current?.focus();
    else if (firstKey === 'password') passwordRef.current?.focus();
    else if (firstKey === 'confirmPassword') confirmPasswordRef.current?.focus();
  };

  const renderField = (fieldName: string) => {
    switch (fieldName) {
      case 'stageName': {
        const showSimilarSection =
          showSimilarProfiles &&
          (isCheckingProfiles || hasSimilarProfilesLoaded || similarProfiles.length > 0);
        const foundCount = similarProfilesCount ?? similarProfiles.length;
        const showSimilarTeaser = showSimilarSection && !isCheckingProfiles && similarProfiles.length > 0;
        return (
          <div
            key="stageName"
            ref={stageNameContainerRef}
            className="relative z-[10000]"
          >
            <Input
              ref={stageNameRef}
              label="Stage name"
              placeholder="Enter your stage name"
              value={formData.stageName}
              onChange={handleStageNameChange}
              error={errors.stageName}
              isLoading={isCheckingProfiles}
            />
            <p className="mt-1 text-xs text-[#8E8E93]">This will be your unique username and cannot be changed later</p>
            {showSimilarTeaser && (
              <button
                type="button"
                className="mt-2 w-full rounded-xl border border-[#2C2C2E] bg-[#141416] px-4 py-3 text-left text-sm text-[#E5E5EA] hover:bg-[#1C1C1E] transition-colors"
                onClick={() => setSimilarProfilesSheetOpen(true)}
              >
                <span className="font-medium">
                  Found {foundCount} similar {foundCount === 1 ? 'profile' : 'profiles'}
                </span>
                <span className="text-[#8E8E93]"> — tap to view</span>
              </button>
            )}

            <Sheet
              open={similarProfilesSheetOpen}
              onOpenChange={setSimilarProfilesSheetOpen}
              title="Similar profiles"
              description="These profiles look similar to your stage name. You can claim your profile or reset password if it's yours."
            >
              <div className="-mt-4">
                <SimilarProfiles
                  profiles={similarProfiles}
                  isLoading={isCheckingProfiles}
                  onClaimProfile={handleClaimProfile}
                  onResetPassword={handleResetPassword}
                  onDismiss={() => setSimilarProfilesSheetOpen(false)}
                  className="border border-[#2C2C2E]"
                  page={similarProfilesPage}
                  totalCount={similarProfilesCount ?? undefined}
                  pageSize={similarProfilesPageSize ?? undefined}
                  onPageChange={(nextPage) => {
                    if (!formData.stageName.trim()) return;
                    void searchSimilarProfiles(formData.stageName, nextPage);
                  }}
                />
              </div>
            </Sheet>
          </div>
        );
      }
      case 'fullName': {
        const applePrefilled = isApplePrefilledField('fullName', formData.fullName);
        return (
          <Input
            key="fullName"
            ref={fullNameRef}
            label="Full name"
            placeholder="Enter your full name"
            value={formData.fullName}
            readOnly={applePrefilled}
            tabIndex={applePrefilled ? -1 : undefined}
            className={applePrefilled ? 'bg-[#1C1C1E] text-[#8E8E93] cursor-default' : undefined}
            onChange={(e) => updateFormData({ fullName: e.target.value })}
            error={errors.fullName}
            trailingAction={applePrefilled ? renderAppleEditAction('fullName') : undefined}
          />
        );
      }
      case 'profileManagedBy':
        return (
          <div key="profileManagedBy" className="">
            <Select
              label="Profile Managed By"
              options={profileManagedByOptions}
              value={formData.profileManagedBy}
              onChange={(value) => updateFormData({ profileManagedBy: value })}
              placeholder="Select"
              error={errors.profileManagedBy}
            />
            {/* Conditional Agency Name Input */}
            <div
              className={`transition-all duration-300 overflow-hidden ${formData.profileManagedBy === 'agency' ? 'max-h-32 opacity-100 pt-2' : 'max-h-0 opacity-0'}`}
            >
              <Input
                ref={agencyNameRef}
                label="Agency Name"
                placeholder="Enter your agency's name"
                value={formData.agencyName || ''}
                onChange={(e) => updateFormData({ agencyName: e.target.value })}
                error={errors.agencyName}
              />
            </div>
            {/* Conditional Relation Input */}
            <div
              className={`transition-all duration-300 overflow-hidden ${formData.profileManagedBy === 'family' ? 'max-h-32 opacity-100 pt-2' : 'max-h-0 opacity-0'}`}
            >
              <Input
                ref={relationRef}
                label="Relation"
                placeholder="e.g. Father, Mother, Manager (Family)"
                value={formData.relation || ''}
                onChange={(e) => updateFormData({ relation: e.target.value })}
                error={errors.relation}
              />
            </div>
          </div>
        );
      case 'phoneNumber':
        return (
          <PhoneInput
            key="phoneNumber"
            ref={phoneRef}
            label="Phone number"
            value={formData.phoneNumber}
            onChange={(value) => updateFormData({ phoneNumber: value })}
            error={errors.phoneNumber}
            isLoading={formData.source === 'google' ? isCheckingGooglePhone : false}
          />
        );
      case 'phone':
        return (
          <Input
            key="phone"
            ref={phoneRef}
            label="Phone number"
            placeholder="Enter your phone number"
            value={formData.phone || ''}
            inputMode="numeric"
            onChange={(e) => {
              const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
              updateFormData({ phone: digits });
              if (errors.phone) {
                setErrors((prev) => {
                  const next = { ...prev };
                  delete next.phone;
                  return next;
                });
              }
            }}
            error={errors.phone}
          />
        );
      case 'email': {
        const applePrefilled = isApplePrefilledField('email', formData.email);
        return (
          <Input
            key="email"
            ref={emailRef}
            label="Email"
            type="email"
            placeholder="Enter your email"
            value={formData.email}
            readOnly={applePrefilled}
            tabIndex={applePrefilled ? -1 : undefined}
            className={applePrefilled ? 'bg-[#1C1C1E] text-[#8E8E93] cursor-default' : undefined}
            onChange={(e) => {
              updateFormData({ email: e.target.value });
              if (errors.email) {
                setErrors((prev) => {
                  const next = { ...prev };
                  delete next.email;
                  return next;
                });
              }
            }}
            error={errors.email}
            trailingAction={applePrefilled ? renderAppleEditAction('email') : undefined}
          />
        );
      }
      case 'password':
        return (
          <Input
            key="password"
            ref={passwordRef}
            label="Password"
            type="password"
            placeholder="Create a password"
            value={formData.password}
            onChange={(e) => updateFormData({ password: e.target.value })}
            error={errors.password}
            showPasswordToggle
          />
        );
      case 'confirmPassword':
        return (
          <Input
            key="confirmPassword"
            ref={confirmPasswordRef}
            label="Confirm Password"
            type="password"
            placeholder="Confirm your password"
            value={formData.confirmPassword}
            onChange={(e) => updateFormData({ confirmPassword: e.target.value })}
            error={errors.confirmPassword}
            showPasswordToggle
          />
        );
      case 'additionalEmail':
        return (
          <Input
            key="additionalEmail"
            ref={additionalEmailRef}
            label="Additional Email"
            type="email"
            placeholder="Enter additional email"
            value={formData.additionalEmail || ''}
            onChange={(e) => updateFormData({ additionalEmail: e.target.value })}
            error={errors.additionalEmail}
          />
        );
      default:
        return null;
    }
  };

  return (
    <AuthLayout showImage={false} backgroundVariant="stars" constrainedHeight>
      <form
        className="flex h-full min-h-0 flex-col space-y-6 px-4 py-8 lg:px-0 lg:py-20 lg:pb-8"
        onSubmit={handleSubmit}
      >
        {/* <Logo size="md" className="mb-4" /> */}
        <div className="shrink-0 space-y-2">
          <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-b from-white to-gray-400 bg-clip-text text-transparent">Almost there!</h1>
          <p className="text-sm text-[#8E8E93]">Just a few more details and you'll be all set</p>
        </div>

        <div className="flex min-h-0 flex-1 flex-col space-y-4 overflow-y-auto overscroll-contain pb-6">
          {activeFields.map(renderField)}
        </div>

        <div className="flex shrink-0 items-center gap-3 pt-2 pb-[calc(env(safe-area-inset-bottom,0px)+1.25rem)] lg:pb-8">
          <Button
            type="button"
            className="shrink-0 px-5 border border-[#3A3A3C] bg-transparent text-white hover:border-[#F33363]/50"
            onClick={onBack}
            disabled={isLoading}
          >
            Back
          </Button>
          <Button
            type="submit"
            variant="active"
            className="flex-1 uppercase tracking-widest"
            isLoading={isLoading}
            disabled={
              isLoading ||
              (formData.source === 'google' && (isCheckingGooglePhone || isGooglePhoneAvailable === false))
            }
          >
            Complete Profile
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </form>

      <ResetPasswordModal
        isOpen={isResetModalOpen}
        onClose={() => { setIsResetModalOpen(false); setResetPasswordProfile(null); }}
        profile={resetPasswordProfile}
        onSuccess={handleResetPasswordSuccess}
      />
    </AuthLayout>
  );
};

// Form data type
export interface SignupFormData {
  email: string;
  password: string;
  confirmPassword: string;
  phoneNumber: string;
  otp: string;
  category: string;
  genres: string[];
  stageName: string;
  fullName: string;
  profileManagedBy: string;
  agencyName?: string;
  relation?: string;
  fetchedCategories?: Category[];
  fetchedSubcategories?: Category[];
  source: "normal" | "google" | "truecaller" | "instagram" | "apple";
  additionalEmail?: string;
  phone?: string; // used for instagram/google if different from phoneNumber
  disableAutoPhoneOtp?: boolean;
}

// Main Signup Flow Component
const ARTIST_USER_TYPES = ['Manager', 'Artist', 'Family'];

// Progress percentages for steps 2–6 and submit states (iOS-style progress feel)
const STEP_PROGRESS: Record<number, number> = {
  2: 24,
  3: 41,
  4: 56,
  5: 68,
  6: 83,
};
const PROGRESS_VALIDATING = 98;
const PROGRESS_COMPLETE = 100;

const getScrollableParent = (node: HTMLElement | null): HTMLElement | null => {
  const authScrollContainer = node?.closest('[data-auth-scroll-container="true"]') as HTMLElement | null;
  if (authScrollContainer) return authScrollContainer;
  let current = node?.parentElement ?? null;
  while (current) {
    const styles = window.getComputedStyle(current);
    const overflowY = styles.overflowY;
    const isScrollable = (overflowY === 'auto' || overflowY === 'scroll') && current.scrollHeight > current.clientHeight;
    if (isScrollable) return current;
    current = current.parentElement;
  }
  return null;
};

interface SignupProgress {
  step: number;
  source: SignupFormData['source'];
  email?: string;
  fullName?: string;
  phoneNumber?: string;
  /** Category slug (step 4+); restored when resuming so onboarding payload has category. */
  category?: string;
  /** Subcategory slugs / genres (step 5+); restored when resuming so onboarding payload has subcategories. */
  subcategories?: string[];
}

export const SignupFlow: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const setShowVideo = useVideoStore((state) => state.setShowVideo);
  const fetchArtistDetails = useArtistDetailsStore((state) => state.fetchArtistDetails);
  const locationState = location.state as {
    startStep?: number;
    source?: SignupFormData['source'];
    fullName?: string;
    email?: string;
    phoneNumber?: string;
    category?: string;
    subcategories?: string[];
  } | undefined;
  const startStep = locationState?.startStep;
  const normalizedStartStep =
    startStep && startStep >= 4 && startStep <= 6
      ? (startStep === 5 ? 4 : startStep)
      : 1;
  const initialStep = normalizedStartStep;
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [direction, setDirection] = useState(1); // 1 = forward, -1 = back
  const [isLoading, setIsLoading] = useState(false);
  const [isSsoLoading, setIsSsoLoading] = useState(false);
  const [progressPercent, setProgressPercent] = useState(STEP_PROGRESS[initialStep] ?? 0);
  const hasAppliedLocationStepRef = useRef(false);
  const hasHydratedPersistedProgressRef = useRef(false);
  const lastFocusedFieldRef = useRef<{ field: HTMLElement | null; at: number }>({ field: null, at: 0 });
  const isNativeAndroid = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
  const [formData, setFormData] = useState<SignupFormData>({
    email: locationState?.email ?? '',
    password: '',
    confirmPassword: '',
    phoneNumber: locationState?.phoneNumber ?? '',
    otp: '',
    category: locationState?.category ?? '',
    genres: locationState?.subcategories ?? [],
    stageName: '',
    fullName: locationState?.fullName ?? '',
    profileManagedBy: '',
    source: locationState?.source ?? 'normal',
  });

  // Transparent body/ancestor so progress bar area is not black (restore on unmount)
  useEffect(() => {
    const prev = document.body.style.backgroundColor;
    document.body.style.backgroundColor = 'transparent';
    return () => {
      document.body.style.backgroundColor = prev || '#0B0B0D';
    };
  }, []);


  // Apply route-provided resume state once (if present).
  useEffect(() => {
    if (hasAppliedLocationStepRef.current) return;
    if (!locationState?.startStep || locationState.startStep < 4 || locationState.startStep > 6) {
      return;
    }
    hasAppliedLocationStepRef.current = true;
    const resumeStep = locationState.startStep === 5 ? 4 : locationState.startStep;
    setCurrentStep(resumeStep);

    const applyResume = async () => {
      const storedApple =
        (locationState.source ?? 'normal') === 'apple' ? await getAppleSignInProfile() : null;
      setFormData((prev) => ({
        ...prev,
        source: locationState.source ?? prev.source,
        fullName: locationState.fullName ?? storedApple?.fullName ?? prev.fullName,
        email: locationState.email ?? storedApple?.email ?? prev.email,
        phoneNumber: locationState.phoneNumber ?? prev.phoneNumber,
        category: locationState.category ?? prev.category,
        genres: locationState.subcategories?.length ? locationState.subcategories : prev.genres,
      }));
    };
    void applyResume();
  }, [locationState?.startStep, locationState?.source, locationState?.fullName, locationState?.email, locationState?.phoneNumber, locationState?.category, locationState?.subcategories]);

  // Hydrate persisted signup progress on hard refresh / direct reopen.
  useEffect(() => {
    if (hasHydratedPersistedProgressRef.current) return;
    if (locationState?.startStep && locationState.startStep >= 4 && locationState.startStep <= 6) return;
    hasHydratedPersistedProgressRef.current = true;

    const hydrate = async () => {
      try {
        const saved = await storageManager.get<SignupProgress>(STORAGE_KEYS.SIGNUP_PROGRESS);
        if (!saved || saved.step < 4 || saved.step > 6) return;
        const resumeStep = saved.step === 5 ? 4 : saved.step;

        setCurrentStep(resumeStep);
        setProgressPercent(STEP_PROGRESS[resumeStep] ?? 0);
        const storedApple = saved.source === 'apple' ? await getAppleSignInProfile() : null;
        setFormData((prev) => ({
          ...prev,
          source: saved.source ?? prev.source,
          email: saved.email ?? storedApple?.email ?? prev.email,
          fullName: saved.fullName ?? storedApple?.fullName ?? prev.fullName,
          phoneNumber: saved.phoneNumber ?? prev.phoneNumber,
          category: saved.category ?? prev.category,
          genres: saved.subcategories?.length ? saved.subcategories : prev.genres,
        }));
      } catch (err) {
        console.error('Failed to hydrate signup progress:', err);
      }
    };

    void hydrate();
  }, [locationState?.startStep]);

  // Persist signup progress for steps 4–6 so user can resume later (including category/subcategories for step 6 payload)
  const persistSignupProgress = useCallback(
    async (step: number) => {
      if (step < 4) return;

      let email = formData.email;
      let fullName = formData.fullName;

      if (formData.source === 'apple') {
        const storedApple = await getAppleSignInProfile();
        let savedProgress: SignupProgress | null = null;
        try {
          savedProgress = await storageManager.get<SignupProgress>(STORAGE_KEYS.SIGNUP_PROGRESS);
        } catch {
          savedProgress = null;
        }
        if (!email.trim()) {
          email = storedApple?.email ?? savedProgress?.email ?? email;
        }
        if (!fullName.trim()) {
          fullName = storedApple?.fullName ?? savedProgress?.fullName ?? fullName;
        }
      }

      const progress: SignupProgress = {
        step,
        source: formData.source,
        email,
        fullName,
        phoneNumber: formData.phoneNumber,
        category: formData.category || undefined,
        subcategories: formData.genres?.length ? formData.genres : undefined,
      };
      try {
        await storageManager.set(STORAGE_KEYS.SIGNUP_PROGRESS, progress);
      } catch (err) {
        console.error('Failed to persist signup progress:', err);
      }
    },
    [formData.email, formData.fullName, formData.phoneNumber, formData.source, formData.category, formData.genres],
  );

  // Control video visibility based on current step
  // Video only shows on Step 1 (email/password)
  useEffect(() => {
    setShowVideo(currentStep === 1);

    // Hide video when leaving signup flow
    return () => {
      setShowVideo(false);
    };
  }, [currentStep, setShowVideo]);

  // Sync progress bar from current step (only when not submitting, so 98%/100% are not overwritten)
  useEffect(() => {
    if (!isLoading && currentStep >= 2 && currentStep <= 6) {
      setProgressPercent(STEP_PROGRESS[currentStep] ?? 0);
    }
  }, [currentStep, isLoading]);

  // Re-hydrate Apple full name on step 6 if it was lost between SSO and profile completion
  useEffect(() => {
    if (currentStep !== 6 || formData.source !== 'apple' || formData.fullName.trim()) return;

    const hydrateAppleName = async () => {
      const storedApple = await getAppleSignInProfile();
      console.log('[AppleSignIn] Step6 hydrate attempt:', {
        currentFullName: formData.fullName,
        storedApple,
      });
      if (!storedApple?.fullName) {
        console.warn('[AppleSignIn] Step6 hydrate skipped: no fullName in storage');
        return;
      }
      setFormData((prev) => {
        const next = {
          ...prev,
          fullName: prev.fullName.trim() ? prev.fullName : storedApple.fullName ?? prev.fullName,
          email: prev.email.trim() ? prev.email : storedApple.email ?? prev.email,
        };
        console.log('[AppleSignIn] Step6 hydrated formData:', {
          fullName: next.fullName,
          email: next.email,
        });
        return next;
      });
    };

    void hydrateAppleName();
  }, [currentStep, formData.source, formData.fullName]);

  // Fetch categories when starting at step 4 (e.g. from login with onboarding_completed=false)
  useEffect(() => {
    if (currentStep === 4 && (!formData.fetchedCategories || formData.fetchedCategories.length === 0)) {
      AuthService.getCategories()
        .then((categories) => {
          setFormData((prev) => ({ ...prev, fetchedCategories: categories }));
        })
        .catch((err) => console.error('Failed to fetch categories:', err));
    }
  }, [currentStep]);

  // Handle browser back/forward navigation to stay within the flow.
  // On native Android onboarding steps (4-6), intercept back to prevent
  // routing away to home/login before onboarding is complete.
  React.useEffect(() => {
    // Push initial state when component mounts
    window.history.replaceState({ step: currentStep }, '', window.location.pathname);

    const handlePopState = (e: PopStateEvent) => {
      if (isNativeAndroid && currentStep >= 4 && currentStep <= 6) {
        // Keep history anchored on signup route while applying in-flow back logic.
        window.history.pushState({ step: currentStep }, '', window.location.pathname);
        if (currentStep === 6) {
          setDirection(-1);
          setCurrentStep(5);
          void persistSignupProgress(5);
          return;
        }
        if (currentStep === 5) {
          setDirection(-1);
          setCurrentStep(4);
          void persistSignupProgress(4);
          return;
        }
        void CapacitorApp.exitApp();
        return;
      }

      // Get step from history state
      const stateStep = (e.state as any)?.step;

      if (stateStep !== undefined && stateStep >= 1 && stateStep <= 6) {
        // Navigate to the step from history
        setCurrentStep(stateStep);
      } else {
        // If no valid state or on first step, navigate to welcome page
        navigate('/');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [currentStep, isNativeAndroid, navigate, persistSignupProgress]);

  // Handle Android hardware back button explicitly in native app.
  useEffect(() => {
    if (!isNativeAndroid) return;

    const listenerPromise = CapacitorApp.addListener('backButton', () => {
      if (currentStep >= 4 && currentStep <= 6) {
        if (currentStep === 6) {
          setDirection(-1);
          setCurrentStep(5);
          void persistSignupProgress(5);
          window.history.pushState({ step: 5 }, '', window.location.pathname);
          return;
        }
        if (currentStep === 5) {
          setDirection(-1);
          setCurrentStep(4);
          void persistSignupProgress(4);
          window.history.pushState({ step: 4 }, '', window.location.pathname);
          return;
        }
        void CapacitorApp.exitApp();
        return;
      }

      // Let existing flow behavior handle non-onboarding steps.
      handleBack();
    });

    return () => {
      void listenerPromise.then((listener) => listener.remove());
    };
  }, [currentStep, isNativeAndroid, persistSignupProgress]);

  const updateFormData = (data: Partial<SignupFormData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
  };

  const handleNext = () => {
    if (currentStep < 6) {
      const nextStep = currentStep + 1;
      setDirection(1); // Forward direction
      setCurrentStep(nextStep);
      // Push new state to history for forward navigation
      window.history.pushState({ step: nextStep }, '', window.location.pathname);

      // Persist progress for category/genres/almost-there steps so user can resume
      void persistSignupProgress(nextStep);
    }
  };

  const handleGoogleSuccess = async (credentialResponse: { credential?: string }) => {
    const credential = credentialResponse.credential;
    if (!credential) {
      toast.error('Google sign-up did not return a credential. Please try again.');
      return;
    }
    setIsSsoLoading(true);
    try {
      const result = await AuthService.signupSso('google', credential);
      await handleSsoResult(result, navigate, fetchArtistDetails);
    } catch (err) {
      toast.error(getAuthErrorMessage(err, 'Google sign-up failed. Please try again.'));
      setIsSsoLoading(false);
    }
  };

  const startTruecallerPolling = async (requestId: string) => {
    const POLL_INTERVAL_MS = 3000;
    const POLL_MAX_ATTEMPTS = 5;

    for (let attempt = 1; attempt <= POLL_MAX_ATTEMPTS; attempt++) {
      try {
        const result = await AuthService.signupSso('truecaller', requestId);
        await handleSsoResult(result, navigate, fetchArtistDetails);
        return;
      } catch (error: any) {
        if (attempt === POLL_MAX_ATTEMPTS) {
          const fallback = 'Truecaller verification did not complete. Please try again or use email/phone.';
          toast.error(getAuthErrorMessage(error, fallback));
          setIsSsoLoading(false);
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      }
    }
  };

  const handleTruecallerSignup = () => {
    if (!canUseTruecaller()) {
      toast.error('Truecaller signup is only available on Android devices.');
      return;
    }

    const requestId = generateTruecallerRequestNonce();
    const deepLink = buildTruecallerDeepLink(requestId);
    if (!deepLink) {
      toast.error('Truecaller is not configured. Please try again later.');
      return;
    }

    setIsSsoLoading(true);

    if (typeof window !== 'undefined') {
      window.location.href = deepLink;
    }

    // Focus checks are unreliable on mobile webviews; always poll backend
    // after deep-link trigger and let backend response determine completion.
    setTimeout(() => {
      void startTruecallerPolling(requestId);
    }, 600);
  };

  const handleInstagramSignup = () => {
    setIsSsoLoading(true);
    InstagramAuthCore.startOAuth('signup').catch((err) => {
      console.error('Instagram signup OAuth error:', err);
      toast.error('Unable to start Instagram signup. Please try again.');
      setIsSsoLoading(false);
    });
  };

  const handleBack = () => {
    if (currentStep > 1) {
      // In Android app onboarding (steps 4-6), keep user in-flow:
      // 6 -> 5, 5 -> 4, and 4 exits app instead of routing away.
      if (isNativeAndroid && currentStep >= 4 && currentStep <= 6) {
        if (currentStep === 6) {
          setDirection(-1);
          setCurrentStep(5);
          window.history.pushState({ step: 5 }, '', window.location.pathname);
          void persistSignupProgress(5);
          return;
        }
        if (currentStep === 5) {
          setDirection(-1);
          setCurrentStep(4);
          window.history.pushState({ step: 4 }, '', window.location.pathname);
          void persistSignupProgress(4);
          return;
        }
        void CapacitorApp.exitApp();
        return;
      }

      // If we started at step 4 (from SSO or login with incomplete onboarding), back from step 4 goes to login
      if (initialStep === 4 && currentStep === 4) {
        navigate('/login');
        return;
      }
      const prevStep = currentStep - 1;
      setDirection(-1); // Back direction
      setCurrentStep(prevStep);
      // Push new state to history for back navigation
      window.history.pushState({ step: prevStep }, '', window.location.pathname);
      if (prevStep >= 4) {
        void persistSignupProgress(prevStep);
      }
    } else {
      // If on first step, navigate to welcome page
      navigate('/');
    }
  };

  const handleSubmit = async () => {
    setProgressPercent(PROGRESS_VALIDATING); // 98% when validation passed and API is about to run
    setIsLoading(true);
    try {
      const payload: any = {
        category: formData.category,
        subcategories: formData.genres,
        professional_name: formData.stageName,
        full_name: formData.fullName,
        profile_managed_by: formData.profileManagedBy as "self" | "family" | "agency" | "manager",
      };

      // Conditional managed_by fields
      if (formData.profileManagedBy === 'agency') payload.agency_name = formData.agencyName;
      if (formData.profileManagedBy === 'family') payload.relation = formData.relation;

      // Source dependent fields
      if (formData.source === 'google') {
        payload.phone = formData.phoneNumber;
      } else if (formData.source === 'truecaller') {
        payload.email = formData.email;
        payload.password = formData.password;
        payload.confirm_password = formData.confirmPassword;
      } else if (formData.source === 'instagram') {
        payload.phone = formData.phone;
        payload.email = formData.email;
        payload.password = formData.password;
        payload.confirm_password = formData.confirmPassword;
      } else if (formData.source === 'apple') {
        payload.phone = formData.phone;
        payload.email = formData.email;
      }

      const res = await AuthService.onboarding(payload);

      setProgressPercent(PROGRESS_COMPLETE); // 100% on success
      toast.success('Onboarding completed!');

      // Clear any stored signup progress once onboarding is fully complete
      try {
        await storageManager.remove(STORAGE_KEYS.SIGNUP_PROGRESS);
        await clearAppleSignInProfile();
      } catch (err) {
        console.error('Failed to clear signup progress after onboarding:', err);
      }

      const userType = (res?.data?.user_type ?? '').trim();

      if (userType.toLowerCase() === 'agency') {
        setAuthRoleCookies('agency');
        navigate('/agency-home');
        return;
      }

      if (ARTIST_USER_TYPES.some((t) => t.toLowerCase() === userType.toLowerCase())) {
        setAuthRoleCookies(userType || 'individual');
        const details = await fetchArtistDetails();
        if (details) {
          await storageManager.set(STORAGE_KEYS.FIRST_TIME_USER_HOME, true);
        }
        navigate('/user-home');
        return;
      }

      // Fallback: e.g. unknown user_type still go to user-home and try to load details
      setAuthRoleCookies(userType || 'individual');
      await fetchArtistDetails();
      await storageManager.set(STORAGE_KEYS.FIRST_TIME_USER_HOME, true);
      navigate('/user-home');
    } catch (error) {
      const message = getAuthErrorMessage(error, 'Failed to complete onboarding. Please try again.');
      toast.error(message);
      console.error('Signup error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const showProgressBar = currentStep >= 2 && currentStep <= 6;

  // Keep step-5 genre selection recoverable if flow is interrupted on this step.
  useEffect(() => {
    if (currentStep === 5) {
      void persistSignupProgress(5);
    }
  }, [currentStep, formData.category, formData.genres, persistSignupProgress]);

  // On mobile keyboard open, keep focused fields visible inside auth scroll containers.
  useEffect(() => {
    const handleFocusIn = (event: FocusEvent) => {
      if (currentStep === 1) return;
      if (typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches) return;

      const target = event.target as HTMLElement | null;
      if (!target) return;

      const isTextField =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        target.isContentEditable;
      if (!isTextField) return;

      const now = Date.now();
      if (lastFocusedFieldRef.current.field === target && now - lastFocusedFieldRef.current.at < 250) return;
      lastFocusedFieldRef.current = { field: target, at: now };

      window.setTimeout(() => {
        const scrollParent = getScrollableParent(target);
        if (!scrollParent) return;

        const targetRect = target.getBoundingClientRect();
        const parentRect = scrollParent.getBoundingClientRect();
        const overflowTop = parentRect.top - targetRect.top + 8;
        const overflowBottom = targetRect.bottom - parentRect.bottom + 16;
        if (overflowTop > 0) {
          scrollParent.scrollBy({ top: -overflowTop, behavior: 'auto' });
          return;
        }
        if (overflowBottom > 0) {
          scrollParent.scrollBy({ top: overflowBottom, behavior: 'auto' });
        }
      }, 180);
    };

    document.addEventListener('focusin', handleFocusIn);
    return () => {
      document.removeEventListener('focusin', handleFocusIn);
    };
  }, [currentStep]);

  return (
    <div className="flex flex-col min-h-[var(--app-height)] overflow-hidden">
      {/* Progress bar: steps 2–6 only, transparent so page background shows */}
      {showProgressBar && (
        <div className="w-full pt-[env(safe-area-inset-top)] shrink-0 bg-transparent">
          <div
            className="mx-auto w-full max-w-xl px-4"
            role="progressbar"
            aria-valuenow={progressPercent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Signup progress"
          >
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-transparent">
              <motion.div
                className="h-full rounded-full bg-gradient-to-r from-[#6366F1] via-[#7C3AED] to-[#EC4899] shadow-[0_0_18px_rgba(129,140,248,0.55)]"
                initial={false}
                animate={{ width: `${progressPercent}%` }}
                transition={{
                  type: 'spring',
                  stiffness: 65,
                  damping: 22,
                  mass: 0.7,
                }}
                style={{
                  willChange: 'width',
                  transformOrigin: 'left',
                  WebkitBackfaceVisibility: 'hidden',
                }}
              />
            </div>
            <div className="mt-2 flex items-center justify-between text-[11px] uppercase tracking-[0.2em] text-white/50">
              <span className="text-white/40">Step {currentStep} of 6</span>
              <span className="font-semibold text-white/80 tabular-nums">
                {Math.round(progressPercent)}%
              </span>
            </div>
          </div>
        </div>
      )}
      {(isLoading || isSsoLoading) && <Loader size="fullscreen" />}
      <AnimatePresence mode="wait" custom={direction}>
        <motion.div
          key={currentStep}
          custom={direction}
          variants={stepVariants}
          initial="enter"
          animate="center"
          exit="exit"
          className="flex flex-1 min-h-0 flex-col bg-[#0B0B0D]"
          style={{
            // GPU acceleration for smooth iOS animations
            willChange: 'opacity, transform',
            WebkitBackfaceVisibility: 'hidden',
            backfaceVisibility: 'hidden',
          }}
        >
          {currentStep === 1 && (
            <Step1
              formData={formData}
              updateFormData={updateFormData}
              onNext={handleNext}
              onBack={handleBack}
              onGoogleSuccess={handleGoogleSuccess}
              onNativeGoogleClick={async () => {
                if (isSsoLoading) return;
                setIsSsoLoading(true);
                try {
                  const idToken = await GoogleAuthCore.signIn();
                  const result = await AuthService.signupSso('google', idToken);
                  await handleSsoResult(result, navigate, fetchArtistDetails);
                } catch (err) {
                  console.error("Google signup OAuth error:", err);
                  toast.error("Unable to start Google sign-up. Please try again.");
                  setIsSsoLoading(false);
                }
              }}
              isSsoLoading={isSsoLoading}
              onSsoStart={() => {
                setIsSsoLoading(true);
              }}
              onTruecallerClick={handleTruecallerSignup}
              onInstagramClick={handleInstagramSignup}
              onAppleClick={async () => {
                if (isSsoLoading) return;
                setIsSsoLoading(true);
                try {
                  const apple = await AppleAuthCore.signIn();
                  console.log('[AppleSignIn] SignupFlow after signIn:', apple);
                  setFormData((prev) => ({
                    ...prev,
                    source: 'apple',
                    email: apple.email ?? prev.email,
                    fullName: apple.fullName ?? prev.fullName,
                  }));
                  const result = await AuthService.signupSso('apple', apple.idToken, {
                    givenName: apple.givenName,
                    familyName: apple.familyName,
                    email: apple.email,
                    fullName: apple.fullName,
                  });
                  await handleSsoResult(result, navigate, fetchArtistDetails, {
                    email: apple.email,
                    fullName: apple.fullName,
                    givenName: apple.givenName,
                    familyName: apple.familyName,
                  });

                  const storedApple = await getAppleSignInProfile();
                  console.log('[AppleSignIn] SignupFlow after SSO storedApple:', storedApple);
                  setFormData((prev) => {
                    const next = {
                      ...prev,
                      source: 'apple' as const,
                      email: storedApple?.email ?? apple.email ?? prev.email,
                      fullName: storedApple?.fullName ?? apple.fullName ?? prev.fullName,
                    };
                    console.log('[AppleSignIn] SignupFlow formData set:', {
                      email: next.email,
                      fullName: next.fullName,
                    });
                    return next;
                  });
                  setDirection(1);
                  setCurrentStep(4);
                  setProgressPercent(STEP_PROGRESS[4] ?? 0);
                  window.history.pushState({ step: 4 }, '', window.location.pathname);
                } catch (err) {
                  console.error("Apple signup OAuth error:", err);
                  toast.error(getAuthErrorMessage(err, "Unable to start Apple sign-up. Please try again."));
                  setIsSsoLoading(false);
                }
              }}
            />
          )}
          {currentStep === 2 && (
            <Step2 formData={formData} updateFormData={updateFormData} onNext={handleNext} onBack={handleBack} />
          )}
          {currentStep === 3 && (
            <Step3 formData={formData} updateFormData={updateFormData} onNext={handleNext} onBack={handleBack} />
          )}
          {currentStep === 4 && (
            <Step4 formData={formData} updateFormData={updateFormData} onNext={handleNext} onBack={handleBack} />
          )}
          {currentStep === 5 && (
            <Step5 formData={formData} updateFormData={updateFormData} onNext={handleNext} onBack={handleBack} />
          )}
          {currentStep === 6 && (
            <Step6
              formData={formData}
              updateFormData={updateFormData}
              onSubmit={handleSubmit}
              isLoading={isLoading}
              onBack={handleBack}
            />
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
